import React, { useEffect, useMemo, useState } from 'react';
import type { MessageThread, User } from '../types';
import ArrowLeftIcon from './icons/ArrowLeftIcon';
import MapPinIcon from './icons/MapPinIcon';

interface ShareLocationPageProps {
  currentUser: User | null;
  threads: MessageThread[];
  activeThreadIds: string[];
  onClose: () => void;
  onShareLocation: (threadIds: string[]) => Promise<boolean> | boolean;
  onStopSharingAll: () => Promise<void> | void;
}

const ShareLocationPage: React.FC<ShareLocationPageProps> = ({
  currentUser,
  threads,
  activeThreadIds,
  onClose,
  onShareLocation,
  onStopSharingAll,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedThreadIds, setSelectedThreadIds] = useState<Set<string>>(new Set(activeThreadIds));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isStopping, setIsStopping] = useState(false);

  useEffect(() => {
    setSelectedThreadIds(new Set(activeThreadIds));
  }, [activeThreadIds]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  const sortedThreads = useMemo(
    () => [...threads].sort((a, b) => (b.timestampMs || 0) - (a.timestampMs || 0)),
    [threads]
  );

  const visibleThreads = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return sortedThreads;

    return sortedThreads.filter((thread) => {
      const otherUsers = (thread.participants || []).filter((participant) => participant.id !== currentUser?.id);
      const directName = otherUsers[0]?.username?.toLowerCase() || '';
      const groupName = thread.groupName?.toLowerCase() || '';
      const participantNames = otherUsers.map((participant) => participant.username?.toLowerCase() || '').join(' ');
      return directName.includes(query) || groupName.includes(query) || participantNames.includes(query);
    });
  }, [currentUser?.id, searchQuery, sortedThreads]);

  const selectedCount = selectedThreadIds.size;
  const hasActiveShares = activeThreadIds.length > 0;

  const toggleThread = (threadId: string) => {
    setSelectedThreadIds((prev) => {
      const next = new Set(prev);
      if (next.has(threadId)) {
        next.delete(threadId);
      } else {
        next.add(threadId);
      }
      return next;
    });
  };

  const getThreadTitle = (thread: MessageThread) => {
    if (thread.isGroup) return thread.groupName || 'Group chat';
    const otherUser = (thread.participants || []).find((participant) => participant.id !== currentUser?.id);
    return otherUser ? `@${otherUser.username}`.toUpperCase() : 'Direct chat';
  };

  const getThreadSubtitle = (thread: MessageThread) => {
    if (thread.isGroup) {
      return `${thread.participants?.length || 0} members`;
    }
    const otherUser = (thread.participants || []).find((participant) => participant.id !== currentUser?.id);
    const fullName = [otherUser?.firstName, otherUser?.lastName].filter(Boolean).join(' ').trim();
    return fullName || '1 to 1 chat';
  };

  const getThreadAvatar = (thread: MessageThread) => {
    if (thread.isGroup) return thread.groupAvatarUrl || '/logo.png';
    const otherUser = (thread.participants || []).find((participant) => participant.id !== currentUser?.id);
    return otherUser?.avatarUrl || '/logo.png';
  };

  const handleShare = async () => {
    if (!selectedCount || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const didStartSharing = await onShareLocation(Array.from(selectedThreadIds));
      if (didStartSharing) {
        onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStopAll = async () => {
    setIsStopping(true);
    try {
      await onStopSharingAll();
      setSelectedThreadIds(new Set());
    } finally {
      setIsStopping(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1300] flex flex-col bg-app-backdrop text-white">
      <header className="sticky top-0 z-10 flex items-center gap-3 border-b border-white/10 bg-app-overlay px-4 pb-3 backdrop-blur-md" style={{ paddingTop: 'calc(var(--sat-native, env(safe-area-inset-top)) + 0.75rem)' }}>
        <button
          onClick={onClose}
          className="rounded-full p-2 transition-colors hover:bg-white/10"
          aria-label="Back"
        >
          <ArrowLeftIcon className="h-6 w-6" />
        </button>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-bold">Share Location</h2>
          <p className="truncate text-xs text-gray-400">Share your live map position only with selected chats</p>
        </div>
        {hasActiveShares && (
          <button
            type="button"
            onClick={handleStopAll}
            disabled={isStopping}
            className="rounded-full border border-red-400/40 bg-red-500/15 px-3 py-2 text-xs font-bold text-red-200 transition hover:bg-red-500/25 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isStopping ? 'Stopping...' : 'Stop All'}
          </button>
        )}
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="mx-auto flex w-full max-w-2xl flex-col gap-4">
          <div className="rounded-[1.75rem] border border-lime-400/20 bg-gradient-to-br from-lime-500/12 via-emerald-500/8 to-white/5 p-4 shadow-2xl shadow-lime-500/10">
            <div className="flex items-center gap-3">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-lime-500 text-black shadow-lg shadow-lime-500/30">
                <MapPinIcon className="h-7 w-7" /> {/* Changed to MapPinIcon for consistency with location sharing */}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white">Private live share</p>
                <p className="text-sm text-gray-300">Pick any mix of groups and direct chats. Only those chats will see you on the map.</p>
              </div>
            </div>
            {hasActiveShares && (
              <div className="mt-4 flex items-center gap-2 rounded-2xl border border-white/10 bg-black/25 px-3 py-3 text-sm text-gray-200">
                <MapPinIcon className="h-5 w-5 text-lime-400" />
                <span>Currently sharing with {activeThreadIds.length} {activeThreadIds.length === 1 ? 'chat' : 'chats'}.</span>
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-white/10 bg-black/40 p-3">
            <input
              type="text"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search chats or groups..."
              className="w-full rounded-xl border border-lime-500/30 bg-white px-4 py-3 text-black placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-lime-500"
            />
          </div>

          <div className="rounded-2xl border border-white/10 bg-black/40 p-3">
            <div className="mb-3 flex items-center justify-between gap-3 px-1">
              <p className="text-sm font-semibold text-white">Chats</p>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-400">{selectedCount} selected</p>
            </div>
            <div className="flex flex-col gap-2">
              {visibleThreads.length > 0 ? (
                visibleThreads.map((thread) => {
                  const isSelected = selectedThreadIds.has(thread.id);
                  return (
                    <button
                      key={thread.id}
                      type="button"
                      onClick={() => toggleThread(thread.id)}
                      className={`flex items-center justify-between gap-3 rounded-2xl border px-3 py-3 text-left transition ${
                        isSelected
                          ? 'border-lime-400 bg-lime-500/12 shadow-lg shadow-lime-500/10'
                          : 'border-white/10 bg-white/5 hover:bg-white/10'
                      }`}
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <img
                          src={getThreadAvatar(thread)}
                          alt={getThreadTitle(thread)}
                          className="h-12 w-12 rounded-2xl border border-white/10 object-cover"
                        />
                        <div className="min-w-0">
                          <p className="truncate font-semibold text-white">{getThreadTitle(thread)}</p>
                          <p className="truncate text-sm text-gray-400">{getThreadSubtitle(thread)}</p>
                        </div>
                      </div>
                      <div
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-black ${
                          isSelected
                            ? 'border-lime-400 bg-lime-400 text-black'
                            : 'border-white/20 bg-transparent text-transparent'
                        }`}
                        aria-hidden="true"
                      >
                        ✓
                      </div>
                    </button>
                  );
                })
              ) : (
                <p className="py-6 text-center text-sm text-gray-400">No chats found.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10 bg-app-overlay px-4 pb-4 pt-3 backdrop-blur-md" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}>
        <div className="mx-auto flex w-full max-w-2xl items-center gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-2xl border border-white/10 px-4 py-3 text-sm font-semibold text-white transition hover:bg-white/5"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleShare}
            disabled={!selectedCount || isSubmitting}
            className="flex-[1.4] rounded-2xl bg-lime-500 px-4 py-3 text-sm font-black text-black transition hover:bg-lime-400 disabled:cursor-not-allowed disabled:bg-gray-700 disabled:text-gray-400"
          >
            {isSubmitting ? 'Sharing...' : `Share With ${selectedCount || ''}`.trim()}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareLocationPage;
